package Utils

import Structs.Client
import Structs.CofcConf
import Structs.OrderProduct
import kotlinx.datetime.LocalDate
import kotlinx.datetime.daysUntil
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlin.math.abs
import kotlin.math.ceil
import kotlin.math.round

/**
 * COFC ordering constraints, shared by every Kotlin client.
 *
 * This is a faithful port of the v2 client link's rule engine (nihulkav-order-v2's
 * `src/lib/dates.ts`, `src/lib/lockState.ts`, `src/lib/cartWarnings.ts` and
 * `QuantityStepper`'s `stepperRules`/`snapQuantity`), so an order placed through the
 * app can be held to exactly the same rules the client sees in the link — see
 * [Structs.CofcConf.agent_constraints].
 *
 * Everything here is pure: [OrderTimingContext] carries the clock, so callers decide
 * "now" and the rules stay unit-testable. Nothing in this file reaches the database.
 *
 * Deliberately NOT ported, because the v2 client does not enforce them either:
 * `cannotOrderDiff`-style weekend-span rules at the date level (v2 exports but never
 * calls them; the server still enforces them), and anything needing a live server
 * figure — monthly obligo (`state == 5`) and `block_rules` debt blocking.
 */
object OrderConstraints {

    /** Start calling a still-open window "closing" this long before it shuts. */
    const val CLOSING_SOON_MINUTES = 6 * 60

    // -----------------------------------------------------------------------
    //  Calendar primitives
    // -----------------------------------------------------------------------

    /** Weekly template dates (בסיס א׳–ז׳), stored as 2010-01-03 .. 2010-01-10. */
    fun isBaseOrderDate(date: String): Boolean {
        val key = DatesManipulator.normalizeToIsoDate(date)
        return key >= "2010-01-03" && key <= "2010-01-10"
    }

    /**
     * Editing a standing base order. Timing, weekday, cutoff and lead-time rules do not apply;
     * catalog/client availability and force-lock still do. Same split as the COFC validator.
     */
    fun isStandingOrderEdit(ctx: OrderTimingContext, date: String): Boolean =
        ctx.baseOrder && isBaseOrderDate(date)

    /**
     * Template weekday for a base date (1=Sunday .. 7=Saturday).
     * 2010-01-04 is בסיס א (Sunday), not its calendar Monday.
     */
    fun baseOrderWeekday(date: String): Int? = when (DatesManipulator.normalizeToIsoDate(date)) {
        "2010-01-03", "2010-01-04" -> 1
        "2010-01-05" -> 2
        "2010-01-06" -> 3
        "2010-01-07" -> 4
        "2010-01-08" -> 5
        "2010-01-09" -> 6
        "2010-01-10" -> 7
        else -> null
    }

    /**
     * Line filter used by COFC new/update before the request is sent.
     * A 2010 template is not a delivery day: [OrderProduct.isLocked] treats it as already past
     * (cutoff, days-ahead, and the calendar weekday of 2010-01-xx), which drops every line and
     * the submit comes back empty. Standing orders keep force-lock and the template weekday only.
     */
    fun cofcSubmitLineLocked(op: OrderProduct?, date: String?, baseOrderEnabled: Boolean): Boolean {
        if (op == null) return false
        if (op.forceLock == 1) return true
        if (baseOrderEnabled && date != null && isBaseOrderDate(date)) {
            val weekday = baseOrderWeekday(date)
            val days = op.daysInt()
            return weekday != null && days.isNotEmpty() && weekday !in days
        }
        return op.isLocked(date)
    }

    /** Israel weekday, 1=Sunday..7=Saturday (matches time1..time7 and the JSON lock maps). */
    fun israelWeekday1to7(date: String): Int = DatesManipulator.getDayOfWeekIsrael(date) % 7 + 1

    /** Client/product delivery-day bitmask test, bit0 = Sunday. */
    fun dayInMask(mask: Int, weekday1to7: Int): Boolean = ((mask shr (weekday1to7 - 1)) and 1) == 1

    /**
     * Day difference that keeps its sign — negative when [to] precedes [from].
     * [DatesManipulator.daysDiffBetweenDates] returns an absolute value, which silently
     * turns "3 days in the past" into "3 days ahead" in a lead-time comparison.
     */
    fun signedDaysDiff(from: String, to: String): Int =
        LocalDate.parse(from.split(" ")[0]).daysUntil(LocalDate.parse(to.split(" ")[0]))

    /**
     * A real cutoff time as "HH:mm", or null when there is none. Empty, "None" and
     * "00:00" are all persisted spellings of "no cutoff" (see [timeLock] and
     * `_parse_hhmm` in ApiCofc2.py), never "closed since midnight".
     */
    fun activeCutoff(raw: String?): String? {
        val t = raw?.trim() ?: return null
        if (t.isEmpty() || t.equals("None", ignoreCase = true) || t.equals("null", ignoreCase = true)) return null
        val parts = t.split(":")
        val h = parts.getOrNull(0)?.trim()?.toIntOrNull() ?: return null
        val m = parts.getOrNull(1)?.trim()?.take(2)?.toIntOrNull() ?: 0
        if (h == 0 && m == 0) return null
        val hh = if (h < 10) "0$h" else h.toString()
        val mm = if (m < 10) "0$m" else m.toString()
        return "$hh:$mm"
    }

    // -----------------------------------------------------------------------
    //  Blocked dates
    // -----------------------------------------------------------------------

    /** Parses [Structs.CofcConf.blocked_dates]: `[{"from","to","label","kind"}]`. */
    fun parseBlockedDates(raw: String?): List<BlockedDateRange> {
        val json = raw?.trim()?.takeIf {
            it.isNotEmpty() && it != "None" && it != "null" && it != "[]"
        } ?: return emptyList()
        return try {
            val arr = Json.parseToJsonElement(json) as? JsonArray ?: return emptyList()
            arr.mapNotNull { element ->
                val obj = element as? JsonObject ?: return@mapNotNull null
                val from = (obj["from"] as? JsonPrimitive)?.content?.takeIf { it.isNotBlank() }
                val to = (obj["to"] as? JsonPrimitive)?.content?.takeIf { it.isNotBlank() }
                // A single-day block may carry only one of the two bounds.
                val start = from ?: to ?: return@mapNotNull null
                val end = to ?: from ?: return@mapNotNull null
                BlockedDateRange(
                    from = start.split(" ")[0],
                    to = end.split(" ")[0],
                    label = (obj["label"] as? JsonPrimitive)?.content?.takeIf { it.isNotBlank() },
                    kind = (obj["kind"] as? JsonPrimitive)?.content?.takeIf { it.isNotBlank() },
                )
            }
        } catch (e: Exception) {
            emptyList()
        }
    }

    /** The blocked range covering [date], or null when the date is free. */
    fun blockedRangeFor(date: String, ranges: List<BlockedDateRange>): BlockedDateRange? {
        val key = date.split(" ")[0]
        return ranges.firstOrNull { key >= it.from && key <= it.to }
    }

    /** Parses [Structs.CofcConf.required_fields]: `[{"field","label","required","approve"}]`. */
    fun parseRequiredFields(raw: String?): List<RequiredOrderField> {
        val json = raw?.trim()?.takeIf {
            it.isNotEmpty() && it != "None" && it != "null" && it != "[]"
        } ?: return emptyList()
        return try {
            val arr = Json.parseToJsonElement(json) as? JsonArray ?: return emptyList()
            arr.mapNotNull { element ->
                val obj = element as? JsonObject ?: return@mapNotNull null
                val field = (obj["field"] as? JsonPrimitive)?.content?.takeIf { it.isNotBlank() }
                    ?: return@mapNotNull null
                RequiredOrderField(
                    field = field,
                    label = (obj["label"] as? JsonPrimitive)?.content?.takeIf { it.isNotBlank() } ?: field,
                    required = truthy((obj["required"] as? JsonPrimitive)?.content),
                    approve = truthy((obj["approve"] as? JsonPrimitive)?.content),
                )
            }
        } catch (e: Exception) {
            emptyList()
        }
    }

    private fun truthy(value: String?): Boolean =
        value == "1" || value.equals("true", ignoreCase = true)

    /**
     * Recurring per-delivery-weekday cutoff from [OrderProduct.delivery_cutoff_rules] or
     * [Structs.CofcConf.delivery_cutoff_rules]:
     * `{"1":{"weekday":5,"time":"14:00"}}` keyed by DELIVERY weekday (1=Sunday..7=Saturday).
     * Returns (cutoffWeekday1to7, "HH:mm") when configured for [deliveryWeekday1to7].
     */
    fun deliveryCutoffRuleFor(raw: String?, deliveryWeekday1to7: Int): Pair<Int, String>? {
        val json = raw?.trim()?.takeIf { it.isNotEmpty() && it != "None" && it != "null" } ?: return null
        return try {
            val entry = (Json.parseToJsonElement(json) as? JsonObject)
                ?.get(deliveryWeekday1to7.toString()) as? JsonObject ?: return null
            val weekday = (entry["weekday"] as? JsonPrimitive)?.content?.toIntOrNull() ?: return null
            val time = (entry["time"] as? JsonPrimitive)?.content?.takeIf { it.isNotBlank() } ?: return null
            if (weekday < 1 || weekday > 7) null else weekday to time
        } catch (e: Exception) {
            null
        }
    }

    /**
     * Evaluates a delivery-weekday cutoff against [target] and the current clock.
     * Null when [raw] has no rule for that weekday.
     */
    fun evaluateDeliveryCutoff(
        raw: String?,
        deliveryWeekday1to7: Int,
        target: String,
        ctx: OrderTimingContext,
    ): DeliveryCutoffEval? {
        val (cutoffWeekday, cutoffTime) = deliveryCutoffRuleFor(raw, deliveryWeekday1to7) ?: return null
        var daysBack = ((deliveryWeekday1to7 - cutoffWeekday) % 7 + 7) % 7
        if (daysBack == 0) daysBack = 7
        val cutoffDate = DatesManipulator.getNextDay(target, -daysBack)
        val locked = when {
            ctx.today > cutoffDate -> true
            ctx.today < cutoffDate -> false
            else -> timePassed(ctx, cutoffTime)
        }
        val normalized = activeCutoff(cutoffTime) ?: cutoffTime
        return DeliveryCutoffEval(
            locked = locked,
            cutoffDate = cutoffDate,
            cutoffTime = normalized,
            deadline = "$cutoffDate $normalized",
        )
    }

    // -----------------------------------------------------------------------
    //  Company ordering window
    // -----------------------------------------------------------------------

    /** The company cutoff in force right now — keyed by the ORDERING day, never the delivery day. */
    fun cutoffForDate(ctx: OrderTimingContext): String? {
        val weekday = israelWeekday1to7(ctx.today)
        val specific = ctx.weekdayCutoffs[weekday]?.trim()?.takeIf { it.isNotEmpty() && it != "None" }
        return activeCutoff(specific ?: ctx.timeClose)
    }

    /** Has today's company cutoff passed? Inclusive: the cutoff minute itself is already closed. */
    fun afterCutoff(ctx: OrderTimingContext): Boolean {
        val cutoff = cutoffForDate(ctx) ?: return false
        return timePassed(ctx, cutoff)
    }

    /** Is [date] a day this client can take delivery on at all (weekday mask + blocked ranges)? */
    fun isDateSelectable(ctx: OrderTimingContext, date: String): Boolean {
        val key = DatesManipulator.normalizeToIsoDate(date)
        return dayInMask(ctx.daysMask, israelWeekday1to7(key)) &&
            blockedRangeFor(key, ctx.blockedRanges) == null
    }

    /** [from] itself when selectable, otherwise the first selectable date after it. */
    fun nextSelectableDate(ctx: OrderTimingContext, from: String): String {
        var cursor = DatesManipulator.normalizeToIsoDate(from)
        repeat(366) {
            if (isDateSelectable(ctx, cursor)) return cursor
            cursor = DatesManipulator.getNextDay(cursor, 1)
        }
        return cursor
    }

    /**
     * The earliest delivery date still reachable right now, given the `daily_close` lead
     * time, `date_control`, weekend/blocked rollover and whether today's cutoff has passed.
     *
     * This single date — not a hardcoded "tomorrow" — is the one the company close time and
     * every per-product hour cutoff apply to. With `daily_close = 2` the boundary is two or
     * more days out, and a cutoff passing there only pushes the boundary, it never closes
     * dates beyond it.
     */
    fun boundaryDate(ctx: OrderTimingContext): String {
        val dailyClose = abs(ctx.dailyCloseRaw)
        val effectiveClose = if (ctx.dateControl == 2) maxOf(0, dailyClose - 1) else dailyClose
        val lead = effectiveClose + (if (afterCutoff(ctx)) 1 else 0)
        return nextSelectableDate(ctx, DatesManipulator.getNextDay(ctx.today, lead))
    }

    /**
     * Today's closing time for [date], or null when no company deadline applies to it.
     * A company delivery-weekday cutoff reports its close time on the cutoff day itself.
     * Otherwise only the boundary date has a same-day deadline, and only while it is still ahead.
     */
    fun companyOrderDeadline(ctx: OrderTimingContext, date: String): String? {
        val target = DatesManipulator.normalizeToIsoDate(date)
        if (isStandingOrderEdit(ctx, target)) return null
        val companyCutoff = evaluateDeliveryCutoff(
            ctx.deliveryCutoffRules, israelWeekday1to7(target), target, ctx,
        )
        if (companyCutoff != null) {
            return if (companyCutoff.cutoffDate == ctx.today) companyCutoff.cutoffTime else null
        }
        if (target != boundaryDate(ctx)) return null
        val cutoff = cutoffForDate(ctx) ?: return null
        return if (timePassed(ctx, cutoff)) null else cutoff
    }

    /** Full pre-check of a delivery date. The server re-validates on submit. */
    fun checkDeliveryDate(ctx: OrderTimingContext, date: String): DateCheck {
        val target = DatesManipulator.normalizeToIsoDate(date)
        if (isStandingOrderEdit(ctx, target)) return DateCheck(true)
        val diffFromToday = signedDaysDiff(ctx.today, target)

        if (diffFromToday < 0) return DateCheck(false, "לא ניתן להזמין לתאריך שעבר")

        val holidayDate = evaluateHolidayDate(
            ctx.productDateWindows, ctx.clientId, ctx.daysMask, target, ctx.now,
        )
        if (holidayDate.reopen) return DateCheck(true)

        if (ctx.dateControl == 1 && diffFromToday <= 0) return DateCheck(false, "יש לבחור תאריך עתידי")

        val blocked = blockedRangeFor(target, ctx.blockedRanges)
        if (blocked != null) {
            val label = blocked.label
            return DateCheck(false, if (label != null) "התאריך חסום — $label" else "תאריך זה חסום להזמנות")
        }

        if (!dayInMask(ctx.daysMask, israelWeekday1to7(target))) {
            return DateCheck(false, "יום זה אינו יום חלוקה")
        }

        // Company-wide per-delivery-weekday cutoff REPLACES daily_close / time_close for that
        // weekday. Absent a rule, the boundary-date lead time still applies.
        val companyCutoff = evaluateDeliveryCutoff(
            ctx.deliveryCutoffRules, israelWeekday1to7(target), target, ctx,
        )
        if (companyCutoff != null) {
            if (companyCutoff.locked) {
                return DateCheck(false, "מחלקת ההזמנות פעילה עד השעה ${companyCutoff.cutoffTime} — לא ניתן להזמין לתאריך זה")
            }
            return DateCheck(true)
        }

        if (signedDaysDiff(boundaryDate(ctx), target) < 0) {
            return DateCheck(false, "מחלקת ההזמנות עובדת מראש — לא ניתן להזמין לתאריך זה")
        }
        return DateCheck(true)
    }

    /** The first date an order can actually be placed for right now. */
    fun firstOrderableDate(ctx: OrderTimingContext): String {
        var cursor = ctx.today
        repeat(366) {
            if (checkDeliveryDate(ctx, cursor).ok) return cursor
            cursor = DatesManipulator.getNextDay(cursor, 1)
        }
        return ctx.today
    }

    /** The next [count] orderable delivery dates, for a date picker that greys out the rest. */
    fun listOrderableDates(ctx: OrderTimingContext, count: Int = 7, lookAhead: Int = 90): List<String> {
        val dates = mutableListOf<String>()
        var cursor = ctx.today
        var scanned = 0
        while (scanned < lookAhead && dates.size < count) {
            if (checkDeliveryDate(ctx, cursor).ok) dates.add(cursor)
            cursor = DatesManipulator.getNextDay(cursor, 1)
            scanned++
        }
        return dates
    }

    // -----------------------------------------------------------------------
    //  Per-product ordering window
    // -----------------------------------------------------------------------

    /**
     * Mirror of the server's product rule. The product cutoff is keyed by today's ordering
     * weekday, while `days_ahead` and both delivery masks apply to the target date.
     */
    fun checkProductDeliveryDate(
        ctx: OrderTimingContext,
        productId: Int,
        product: OrderProduct,
        available: Int,
        date: String,
    ): ProductDateCheck {
        val target = DatesManipulator.normalizeToIsoDate(date)
        if (product.forceLock == 1 || available == 0) return ProductDateCheck(false, "לא יסופק")
        if (isStandingOrderEdit(ctx, target)) {
            val weekday = baseOrderWeekday(target)
            val productDays = product.daysInt()
            if (weekday != null && productDays.isNotEmpty() && weekday !in productDays) {
                return ProductDateCheck(false, "המוצר אינו זמין ביום זה")
            }
            return ProductDateCheck(true)
        }

        when (holidayProductVerdict(ctx.productDateWindows, productId, ctx.clientId, ctx.daysMask, target, ctx.now)) {
            HolidayProductVerdict.DENY, HolidayProductVerdict.EXCLUSIVE_LOCK ->
                return ProductDateCheck(
                    false,
                    "המוצר כבר אינו זמין בתאריך זה",
                    nextDate = nextProductDate(ctx, product, target),
                    hard = true,
                )
            HolidayProductVerdict.BYPASS -> {
                val until = evaluateHolidayDate(
                    ctx.productDateWindows, ctx.clientId, ctx.daysMask, target, ctx.now,
                ).orderUntil
                return ProductDateCheck(true, deadline = until)
            }
            HolidayProductVerdict.KEEP -> { /* fall through to the regular card rules */ }
        }

        val holidayReopen = holidayReopensDeliveryDate(
            ctx.productDateWindows, ctx.clientId, ctx.daysMask, target, ctx.now,
        )
        // A closed calendar day is not a cutoff. unlock_p must not turn it into
        // "order it and we'll move it". A live holiday window that reopens the
        // date is the only exception, and it was already applied above.
        if (!holidayReopen) {
            val blocked = blockedRangeFor(target, ctx.blockedRanges)
            if (blocked != null) {
                val label = blocked.label?.takeIf { it.isNotBlank() }
                return ProductDateCheck(
                    ok = false,
                    reason = if (label != null) "התאריך חסום — $label" else "תאריך זה חסום להזמנות",
                    nextDate = nextProductDate(ctx, product, target),
                    hard = true,
                )
            }
        }
        val targetWeekday = israelWeekday1to7(target)
        val productDays = product.daysInt()
        if ((!holidayReopen && !dayInMask(ctx.daysMask, targetWeekday)) ||
            (productDays.isNotEmpty() && !productDays.contains(targetWeekday))
        ) {
            return ProductDateCheck(
                ok = false,
                reason = "המוצר אינו זמין ביום זה",
                nextDate = nextProductDate(ctx, product, target),
                hard = true,
            )
        }

        // A recurring delivery-weekday cutoff REPLACES the days_ahead/hourLock chain below for
        // that delivery weekday, on any future date rather than only the boundary date.
        // Product-level rule wins; otherwise the company-wide cofc_conf rule.
        val cutoffEval = evaluateDeliveryCutoff(product.delivery_cutoff_rules, targetWeekday, target, ctx)
            ?: evaluateDeliveryCutoff(ctx.deliveryCutoffRules, targetWeekday, target, ctx)
        if (cutoffEval != null) {
            if (cutoffEval.locked) {
                return ProductDateCheck(
                    ok = false,
                    reason = "עבר זמן ההזמנה למוצר זה (${cutoffEval.cutoffTime})",
                    nextDate = nextProductDate(ctx, product, target),
                )
            }
            return ProductDateCheck(true, deadline = cutoffEval.deadline)
        }

        if (signedDaysDiff(ctx.today, target) < product.days_ahead) {
            return ProductDateCheck(
                ok = false,
                reason = "מוצר זה מוזמן ${product.days_ahead} ימים מראש",
                nextDate = nextProductDate(ctx, product, target),
                hard = true,
            )
        }

        val deadlines = mutableListOf<String>()
        if (product.days_ahead > 0) {
            deadlines.add("${DatesManipulator.getNextDay(target, -product.days_ahead)} 23:59")
        }

        // cutoff precedence: this product's per-ordering-weekday override -> its flat hourLock
        // -> the company-wide default for the same weekday.
        val orderWeekday = israelWeekday1to7(ctx.today)
        val rawCutoff = weekdayHourMapValue(product.day_hour_locks, orderWeekday)?.takeIf { it.isNotBlank() }
            ?: product.hourLock.takeIf { it.isNotBlank() }
            ?: weekdayHourMapValue(ctx.dayTimeLocks, orderWeekday)
        val cutoff = activeCutoff(rawCutoff)
        if (cutoff != null && target == boundaryDate(ctx)) {
            if (timePassed(ctx, cutoff)) {
                return ProductDateCheck(
                    ok = false,
                    reason = "עבר זמן ההזמנה למוצר זה ($cutoff)",
                    nextDate = nextProductDate(ctx, product, target),
                )
            }
            deadlines.add("${ctx.today} $cutoff")
        }
        return ProductDateCheck(true, deadline = deadlines.minOrNull())
    }

    /** The next date after [from] this product could be delivered on, ignoring cutoffs. */
    private fun nextProductDate(ctx: OrderTimingContext, product: OrderProduct, from: String): String? {
        val productDays = product.daysInt()
        var cursor = from
        repeat(90) {
            cursor = DatesManipulator.getNextDay(cursor, 1)
            val weekday = israelWeekday1to7(cursor)
            if (dayInMask(ctx.daysMask, weekday) &&
                (productDays.isEmpty() || productDays.contains(weekday)) &&
                blockedRangeFor(cursor, ctx.blockedRanges) == null
            ) {
                return cursor
            }
        }
        return null
    }

    /**
     * How a product's ordering window should be presented, in one verdict.
     *
     * [legacyLocked] is the existing `NKDBInterface.isProductLocked` answer and
     * [nextSupplyFallback] its `getNextSupplyDate`. Both are folded in exactly the way the v2
     * link does it: the product must clear this engine AND the legacy check, and when it does
     * not, `unlock_p` may still soften a lock into one the user can acknowledge.
     *
     * [nextSupplyFallback] is only invoked for a product that is actually locked, so callers
     * may pass a lookup that walks the calendar without paying for it on every open row.
     */
    fun evaluateProductLock(
        ctx: OrderTimingContext,
        productId: Int,
        product: OrderProduct,
        available: Int,
        date: String,
        legacyLocked: Boolean = false,
        nextSupplyFallback: () -> String? = { null },
    ): ProductLockState {
        if (product.forceLock == 1 || available == 0) {
            return ProductLockState(
                level = ProductLockLevel.HARD,
                locked = true,
                needsAcknowledgement = false,
                reason = "המוצר אינו זמין לאספקה",
            )
        }
        // Base template: availability (above) and the template weekday only.
        // Cutoff, days-ahead and the legacy calendar-weekday lock do not apply.
        if (isStandingOrderEdit(ctx, date)) {
            val local = checkProductDeliveryDate(ctx, productId, product, available, date)
            if (!local.ok) {
                return ProductLockState(
                    level = ProductLockLevel.HARD,
                    locked = true,
                    needsAcknowledgement = false,
                    reason = local.reason ?: "המוצר אינו זמין ביום זה",
                )
            }
            return OPEN
        }

        val local = checkProductDeliveryDate(ctx, productId, product, available, date)
        if (local.hard && !local.ok) {
            return ProductLockState(
                level = ProductLockLevel.HARD,
                locked = true,
                needsAcknowledgement = false,
                reason = local.reason ?: "המוצר כבר אינו זמין בתאריך זה",
                nextDate = local.nextDate,
            )
        }
        if (local.ok && !legacyLocked) {
            val deadline = local.deadline ?: return OPEN
            val remaining = minutesUntil(ctx, deadline)
            if (remaining > CLOSING_SOON_MINUTES) return OPEN
            return ProductLockState(
                level = ProductLockLevel.CLOSING,
                locked = false,
                needsAcknowledgement = false,
                deadline = deadline,
            )
        }

        val next = local.nextDate ?: nextSupplyFallback()
        val cause = local.reason ?: "המוצר אינו זמין בתאריך שנבחר"
        val nextText = if (next != null) " זמין שוב ב-${DatesManipulator.dateIsrael(next)}." else ""
        val locked = ctx.unlockP == 0 || next == null
        return ProductLockState(
            level = if (locked) ProductLockLevel.HARD else ProductLockLevel.SOFT,
            locked = locked,
            needsAcknowledgement = !locked,
            reason = "$cause.$nextText".replace("..", ".").trim(),
            nextDate = next,
        )
    }

    // -----------------------------------------------------------------------
    //  Quantity rules
    // -----------------------------------------------------------------------

    /**
     * Minimum and step only bind the product's first (base) unit, and never in returns mode —
     * a second/third unit quantity is a package count the conversion rate already governs.
     */
    fun quantityRules(product: OrderProduct, isFirstUnit: Boolean, returnsMode: Boolean): QuantityRules {
        if (returnsMode || !isFirstUnit) return QuantityRules(0f, 0f)
        return QuantityRules(step = product.step, min = product.min_order)
    }

    /** Rounds [value] onto the nearest permitted step, then up to the minimum. */
    fun snapQuantity(value: Float, step: Float, min: Float): Float {
        if (value <= 0f || value.isNaN()) return 0f
        var next = value
        if (step > 0f) {
            next = round3(round(next / step) * step)
            if (next == 0f) next = step
        }
        if (min > 0f && next < min) {
            next = if (step > 0f) round3(ceil(min / step) * step) else min
        }
        return next
    }

    /**
     * Availability the COFC order check uses: catalog `order_products.available` and the
     * client's `client_order_available` row must both be 1. A missing client row uses
     * [missingDefault] — 1 on an open list, 0 when `inverse_avail` is on or the client
     * inherits a closed availability menu. Null catalog availability means there is no
     * order-product row, which the server does not treat as unavailable.
     */
    fun effectiveOrderAvailable(clientAvailable: Int?, catalogAvailable: Int?, missingDefault: Int = 1): Int {
        val clientFlag = clientAvailable ?: missingDefault
        val catalogFlag = catalogAvailable ?: 1
        return if (clientFlag == 1 && catalogFlag == 1) 1 else 0
    }

    /** Per-line problems: availability, then the product's own minimum and step. */
    fun lineWarnings(
        ctx: OrderTimingContext,
        productId: Int,
        product: OrderProduct,
        available: Int,
        date: String,
        /** Quantity expressed in the product's base unit (second unit × conversion rate). */
        primaryQuantity: Float,
        /** Quantity as typed, in the unit actually selected on the line. */
        enteredQuantity: Float,
        isFirstUnit: Boolean,
        returnsMode: Boolean = false,
        legacyLocked: Boolean = false,
        nextSupplyFallback: () -> String? = { null },
        unitLabel: String = "",
    ): List<String> {
        if (returnsMode) return emptyList()
        val warnings = mutableListOf<String>()

        val lock = evaluateProductLock(ctx, productId, product, available, date, legacyLocked, nextSupplyFallback)
        lock.reason?.let { reason ->
            warnings.add(if (lock.needsAcknowledgement) "$reason $SOFT_LOCK_SUFFIX" else reason)
        }

        if (product.min_order > 0f && primaryQuantity < product.min_order) {
            warnings.add("מינימום ${decToString(product.min_order)} $unitLabel".trim())
        }

        val rules = quantityRules(product, isFirstUnit, returnsMode)
        if (rules.step > 1f && enteredQuantity > 0f) {
            val remainder = abs(enteredQuantity / rules.step - round(enteredQuantity / rules.step))
            if (remainder > 0.0005f) {
                warnings.add("בקפיצות של ${decToString(rules.step)}")
            }
        }
        return warnings
    }

    /**
     * Order-level problems, in the order the v2 checkout reports them. Returns the first
     * blocking message, or null when the order may be sent.
     *
     * [lineProblems] is how many cart lines still have a blocking [lineWarnings] entry.
     * Rules the client link applies to its own checkout only — credit-card payment,
     * BTC buyer details, the returns photo, the pre-order popup and the empty-cart block —
     * are intentionally not part of this.
     *
     * Forced address (`static_address` in [extraFields]) mirrors v2:
     * - [selfPickup] skips the address requirement and any approve checkbox
     * - a blank override is OK when [clientHasAddress] (client card already has an address)
     * - [addressApproved] is required when the field's `approve` flag is on and not pickup
     *
     * Forced business name (`static_business_name`) mirrors v2:
     * - a blank override is OK when [clientHasBusinessName]
     * - an empty client card requires a typed name whenever the field is configured
     */
    fun checkOrderTotals(
        ctx: OrderTimingContext,
        date: String,
        lineProblems: Int = 0,
        totalMoney: Float = 0f,
        totalUnits: Float = 0f,
        notes: String = "",
        extraFields: Map<String, String> = emptyMap(),
        selfPickup: Boolean = false,
        clientHasAddress: Boolean = false,
        addressApproved: Boolean = false,
        clientHasBusinessName: Boolean = true,
        /**
         * Set when an existing order is being updated and a holiday/product window already
         * covers this date. The window decides which products lock; a blocked date or a
         * missed delivery day must not reject the rest of the save. A new order, and an
         * update on a date with no window, still fail here when the date is closed.
         */
        skipClosedDate: Boolean = false,
    ): String? {
        if (ctx.minOrderSum > 0f && totalMoney < ctx.minOrderSum) {
            return "לא עברת את מינימום ההזמנה בכסף ${decToString(ctx.minOrderSum)} ₪"
        }
        if (ctx.minOrderSum < 0f && totalMoney > abs(ctx.minOrderSum)) {
            return "עברת את תקציב ההזמנה ${decToString(abs(ctx.minOrderSum))} ₪"
        }
        if (ctx.minOrderSum == 0f && ctx.minOrderQty > 0 && totalUnits < ctx.minOrderQty) {
            return "לא עברת את מינימום ההזמנה ${ctx.minOrderQty}"
        }
        if (ctx.needNotes == 1 && notes.isBlank()) return "חובה למלא הערה להזמנה"

        if (!skipClosedDate) {
            val dateCheck = checkDeliveryDate(ctx, date)
            if (!dateCheck.ok) return dateCheck.reason ?: "תאריך האספקה נסגר להזמנות"
        }

        ctx.requiredFields.forEach { field ->
            if (field.field == "static_business_name") {
                val raw = extraFields[field.field]?.trim().orEmpty()
                if (raw.isEmpty() && !clientHasBusinessName) {
                    val label = field.label.ifBlank { "שם עסק מאולץ" }
                    return "חובה למלא: $label"
                }
                return@forEach
            }
            if (field.field == "static_address") {
                if (selfPickup) return@forEach
                val raw = extraFields[field.field]?.trim().orEmpty()
                if (field.required && raw.isBlank() && !clientHasAddress) {
                    return "חובה לבחור כתובת: ${field.label}"
                }
                if (field.approve && !addressApproved) {
                    return "יש לאשר את הכתובת: ${field.label}"
                }
                return@forEach
            }
            if (field.required && extraFields[field.field]?.isNotBlank() != true) {
                return "חובה למלא: ${field.label}"
            }
        }

        if (lineProblems > 0) {
            return if (lineProblems == 1) "יש בעיה בפריט אחד בסל" else "יש בעיות ב-$lineProblems פריטים בסל"
        }
        return null
    }

    // -----------------------------------------------------------------------
    //  Internals
    // -----------------------------------------------------------------------

    private val OPEN = ProductLockState(
        level = ProductLockLevel.OPEN,
        locked = false,
        needsAcknowledgement = false,
    )

    /** Marks a soft (`unlock_p`) lock in a line warning, and identifies it again when filtering. */
    const val SOFT_LOCK_SUFFIX = "לא יסופק לתאריך שנבחר"

    /** Inclusive: the cutoff minute itself counts as passed, matching [timeLock]. */
    private fun timePassed(ctx: OrderTimingContext, hhmm: String): Boolean {
        val parts = hhmm.split(":")
        val hour = parts.getOrNull(0)?.trim()?.toIntOrNull() ?: return false
        val minute = parts.getOrNull(1)?.trim()?.take(2)?.toIntOrNull() ?: 0
        return ctx.nowHour > hour || (ctx.nowHour == hour && ctx.nowMinute >= minute)
    }

    /** Minutes from "now" until a "yyyy-MM-dd HH:mm" deadline; negative once it has passed. */
    private fun minutesUntil(ctx: OrderTimingContext, deadline: String): Int {
        val parts = deadline.split(" ")
        val day = parts.getOrNull(0) ?: return 0
        val time = (parts.getOrNull(1) ?: "00:00").split(":")
        val hour = time.getOrNull(0)?.toIntOrNull() ?: 0
        val minute = time.getOrNull(1)?.toIntOrNull() ?: 0
        val dayMinutes = signedDaysDiff(ctx.today, day) * 24 * 60
        return dayMinutes + (hour * 60 + minute) - (ctx.nowHour * 60 + ctx.nowMinute)
    }

    private fun round3(value: Float): Float = round(value * 1000f) / 1000f

    /** Trims a trailing ".0" so "מינימום 2" never reads "מינימום 2.0". */
    fun decToString(value: Float): String =
        if (value == value.toLong().toFloat()) value.toLong().toString() else round3(value).toString()
}

/** One blocked ordering window from [Structs.CofcConf.blocked_dates]. */
data class BlockedDateRange(
    val from: String,
    val to: String,
    val label: String? = null,
    val kind: String? = null,
)

/** One forced checkout field from [Structs.CofcConf.required_fields]. */
data class RequiredOrderField(
    val field: String,
    val label: String,
    val required: Boolean = false,
    val approve: Boolean = false,
)

data class DateCheck(val ok: Boolean, val reason: String? = null)

/** Result of evaluating one delivery-weekday cutoff rule against a target date. */
data class DeliveryCutoffEval(
    val locked: Boolean,
    val cutoffDate: String,
    val cutoffTime: String,
    /** "yyyy-MM-dd HH:mm" at which this rule shuts the target date. */
    val deadline: String,
)

data class ProductDateCheck(
    val ok: Boolean,
    val reason: String? = null,
    val nextDate: String? = null,
    /** "yyyy-MM-dd HH:mm" at which this product's window for the target date shuts. */
    val deadline: String? = null,
    /**
     * unlock_p must not soften this failure. Hard covers a locked date, a holiday
     * deny or exclusive catalog, a delivery weekday the product does not serve,
     * and days-ahead. A passed hour or delivery-weekday cutoff stays soft.
     */
    val hard: Boolean = false,
)

/**
 * - [OPEN] orderable, nothing to say
 * - [CLOSING] orderable, but the window shuts at a known time
 * - [SOFT] past the window, yet `unlock_p` allows ordering once the user acknowledges
 *   it will be supplied later
 * - [HARD] cannot be ordered for the selected delivery date
 */
enum class ProductLockLevel { OPEN, CLOSING, SOFT, HARD }

data class ProductLockState(
    val level: ProductLockLevel,
    val locked: Boolean,
    val needsAcknowledgement: Boolean,
    val reason: String? = null,
    val nextDate: String? = null,
    val deadline: String? = null,
)

data class QuantityRules(val step: Float, val min: Float)

/**
 * Everything the constraint rules need, resolved once per screen. [now] carries the clock so
 * the rules are pure and testable; pass an Israel-local "yyyy-MM-dd HH:mm:ss".
 */
data class OrderTimingContext(
    /** Client delivery-day bitmask, bit0 = Sunday. */
    val daysMask: Int,
    val blockedRanges: List<BlockedDateRange> = emptyList(),
    val dateControl: Int = 0,
    /** Raw [Structs.CofcConf.daily_close]; a negative value is the legacy weekend-block spelling. */
    val dailyCloseRaw: Int = 1,
    val timeClose: String? = null,
    /** Per-ordering-weekday company cutoff, 1=Sunday..7=Saturday (`time1`..`time7`). */
    val weekdayCutoffs: Map<Int, String?> = emptyMap(),
    val dayTimeLocks: String? = null,
    /** Raw [Structs.CofcConf.delivery_cutoff_rules] JSON. */
    val deliveryCutoffRules: String? = null,
    val productDateWindows: String? = null,
    val clientId: Int = 0,
    val unlockP: Int = 0,
    val needNotes: Int = 0,
    val requiredFields: List<RequiredOrderField> = emptyList(),
    val minOrderQty: Int = 0,
    val minOrderSum: Float = 0f,
    /** [Structs.CofcConf.base_order] == 1. Weekly template dates then skip timing locks. */
    val baseOrder: Boolean = false,
    val now: String = DatesManipulator.dateNow(),
) {
    val today: String = now.split(" ")[0]
    val nowHour: Int = DatesManipulator.getTime(now).split(":").getOrNull(0)?.toIntOrNull() ?: 0
    val nowMinute: Int = DatesManipulator.getTime(now).split(":").getOrNull(1)?.toIntOrNull() ?: 0

    companion object {
        /**
         * Resolves the context for one client from the company COFC config. [now] defaults to
         * the Israel wall clock the cutoff rules are written in.
         */
        fun of(
            conf: CofcConf?,
            client: Client?,
            now: String = DatesManipulator.dateNow(),
        ): OrderTimingContext {
            val today = now.split(" ")[0]
            // days == -1 means "every day" in older client rows.
            val rawDays = client?.getDays(today) ?: 127
            return OrderTimingContext(
                daysMask = if (rawDays == -1) 127 else rawDays,
                blockedRanges = OrderConstraints.parseBlockedDates(conf?.blocked_dates),
                dateControl = conf?.date_control ?: 0,
                dailyCloseRaw = conf?.daily_close ?: 1,
                timeClose = conf?.time_close,
                weekdayCutoffs = mapOf(
                    1 to conf?.time1, 2 to conf?.time2, 3 to conf?.time3, 4 to conf?.time4,
                    5 to conf?.time5, 6 to conf?.time6, 7 to conf?.time7,
                ),
                dayTimeLocks = conf?.day_time_locks,
                deliveryCutoffRules = conf?.delivery_cutoff_rules,
                productDateWindows = conf?.product_date_windows,
                clientId = client?.id ?: 0,
                unlockP = conf?.unlock_p ?: 0,
                needNotes = conf?.need_notes ?: 0,
                requiredFields = OrderConstraints.parseRequiredFields(conf?.required_fields),
                minOrderQty = client?.min_order ?: 0,
                minOrderSum = client?.min_order_sum ?: 0f,
                baseOrder = conf?.base_order == 1,
                now = now,
            )
        }
    }
}
